<template>
    <div class="person-card">
      <div class="person-info">
        <div class="avatar" :style="{ backgroundColor: avatarColor }">
          {{ initials }}
        </div>
        <h2>{{ person.user_name }}</h2>
      </div>
      <div class="status-toggle">
        <span class="status-label">{{ person.is_home ? 'En Casa' : 'Fuera' }}</span>
        <label class="switch">
          <input type="checkbox" v-model="is_home" @change="updateStatus" :disabled="store.id_user !== person.id_user">
          <span class="slider"></span>
        </label>
      </div>
    </div>
  </template>
  
  <script setup>
  import { computed, ref, watch, defineProps, defineEmits } from 'vue';
  import { useAuth }from "@/store/auth"

  const store = useAuth()
  
  const props = defineProps({
    person: {
      type: Object,
      required: true
    }
  });
  
  const emit = defineEmits(['update:status']);
  
  // Computed properties for the avatar
  const initials = computed(() => {
    return props.person.user_name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase();
  });
  
  const avatarColor = computed(() => {
    let hash = 0;
    for (let i = 0; i < props.person.user_name.length; i++) {
      hash = props.person.user_name.charCodeAt(i) + ((hash << 5) - hash);
    }
    
    const hue = hash % 360;
    return `hsl(${hue}, 70%, 60%)`;
  });
  
  // Two-way binding for isHome status
  const is_home = ref(props.person.is_home);
  
  // Watch for changes from parent
  watch(() => props.person.is_home, (newValue) => {
    is_home.value = newValue;
  });
  
  // Emit changes to parent
  const updateStatus = () => {
    emit('update:status', {
      id_user: props.person.id_user,
      is_home: is_home.value
    });
  };
  </script>
  
  <style scoped>
  .person-card {
    display: flex;
    justify-content: space-between;
    align-items: center;
    background-color: #fff;
    border-radius: 10px;
    padding: 15px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    transition: transform 0.2s ease;
  }
  
  .person-card:hover {
    transform: translateY(-2px);
  }
  
  .person-info {
    display: flex;
    align-items: center;
    gap: 15px;
  }
  
  .avatar {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-weight: bold;
  }
  
  .person-info h2 {
    margin: 0;
    font-size: 1.2rem;
  }
  
  .status-toggle {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  
  .status-label {
    font-size: 0.9rem;
    width: 60px;
    text-align: right;
  }
  
  /* Toggle switch styling */
  .switch {
    position: relative;
    display: inline-block;
    width: 50px;
    height: 24px;
  }
  
  .switch input {
    opacity: 0;
    width: 0;
    height: 0;
  }
  
  .slider {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: #ccc;
    transition: .4s;
    border-radius: 24px;
  }
  
  .slider:before {
    position: absolute;
    content: "";
    height: 16px;
    width: 16px;
    left: 4px;
    bottom: 4px;
    background-color: white;
    transition: .4s;
    border-radius: 50%;
  }
  
  input:checked + .slider {
    background-color: #42b983;
  }
  
  input:checked + .slider:before {
    transform: translateX(26px);
  }
  
  /* Responsive adjustments */
  @media (max-width: 480px) {
    .person-card {
      flex-direction: column;
      align-items: flex-start;
      gap: 15px;
    }
    
    .status-toggle {
      width: 100%;
      justify-content: space-between;
    }
    
    .status-label {
      text-align: left;
    }
  }
  </style>